import dateFormatFunc from 'dateformat';
import { flatTreeProjects, overlap, orderDevices, isUndefinedOrNull, getFullTimeFromTimeDelta } from '#helper';

const getDevicesToAssign = (parent = {}, devices) =>
  devices.reduce((a, c) => {
    if (c.projectId === parent.id || c.projects?.includes(parent.id)) {
      const { projectId, id, uid, title, type, showAlertIcon, alertOn } = c;
      a.push({
        title,
        type,
        projectId,
        id,
        uid,
        disabledBy: null,
        showAlertIcon,
        alertOn
      });
    }
    return a;
  }, []);

export const getDevices = (currentProject, projects, devices) => {
  const flattedProjects = flatTreeProjects(projects);
  let parent = flattedProjects.find(p => p.id === currentProject.parentId);
  // let's hope it's the root project
  if (!parent) {
    [parent] = flattedProjects;
    // if a parent is the root, nothing to compare with
    return orderDevices(getDevicesToAssign(parent, devices));
  }
  let devicesToAssign = getDevicesToAssign(parent, devices);

  const getDate = date => (date ? new Date(date) : new Date());
  parent.children.forEach(c => {
    if (c.id !== currentProject.id) {
      const isOverlap = overlap(
        new Date(c.beginDate),
        getDate(c.expirationDate),
        new Date(currentProject.beginDate),
        getDate(currentProject.expirationDate)
      );

      if (isOverlap) {
        const devicesToCompare = devices.filter(d =>
          d.projectId === c.id || d.projects?.includes(c.id));
        devicesToAssign = devicesToAssign.map(d => {
          const device = devicesToCompare.find(dC => dC.id === d.id);
          if (device) {
            return {
              ...d,
              disabledBy: c
            };
          }
          return d;
        });
      }
    }
  });
  return orderDevices(devicesToAssign);
};

const buildStringDate = (date, format) => {
  const d = date.split(/\.|\//);
  const formatFirstChar = format && format.charAt(0) && format.charAt(0).toLowerCase(0);
  return formatFirstChar === 'm' ? `${d[2]}-${d[0]}-${d[1]}` : `${d[2]}-${d[1]}-${d[0]}`;
};

/* #### getCurrentProject
 * assembles and returns object when user adds new project
 */
export const getCurrentProject = (currentProject, formValues, dateFormat) => {
  let project = currentProject;
  if (!currentProject) {
    let { beginDate, expirationDate } = formValues;
    const { parentId } = formValues;

    beginDate = buildStringDate(beginDate, dateFormat);
    if (expirationDate) {
      expirationDate = buildStringDate(expirationDate, dateFormat);
    }

    project = {
      id: parentId,
      parentId,
      beginDate,
      expirationDate
    };
  }
  return project;
};

export const mapDevicesToSelect = devices => devices.map(d => {
  if (Number.isInteger(d)) {
    return {
      value: d,
      id: d
    };
  }

  return {
    value: d.id,
    label: d.title || d.uid,
    ...d
  };
});

const formattedCustomFields = customFields => customFields.map(customField => {
  const { label, customFieldValues, id } = customField;
  const formattedValue = customFieldValues?.flatMap(value => value.customFieldValue || []);

  return {
    id: Number.isInteger(id) ? id : undefined,
    label,
    value: formattedValue?.length ? formattedValue : undefined
  };
});

export const setDataPost = (values = {}, isRootProject) => {
  const projectModules = values.modules ?? [];

  const res = {
    title: values.title,
    parentId: values.parentId,
    beginDate: values.beginDate,
    isExpirationDate: values.isExpirationDate,
    devices: values.devices,
    users: values.users,
    disableIsExpirationDate: values.disableIsExpirationDate,
    description: values.description,
    alerts: values.alerts,
    products: values.products,
    packages: values.packages,
    alertConfirmations: values.alertConfirmations,
    customFields: formattedCustomFields(values.customFields ?? [])
  };

  if (projectModules.length && !isRootProject) {
    res.modules = projectModules.flatMap(pm => (pm.checked ? pm.id : []));
  }

  if (+values.isExpirationDate) {
    res.expirationDate = values.expirationDate;
  }

  return res;
};

// The same as ISOString format but without timezone
// new Date('2-08-11T00:00:00') => Invalid Date
export const isoDateOnlyGen = d => `0000${dateFormatFunc(d, 'isoDate')}T00:00:00`.slice(-19);

// Project modules
const initialModules = [
  {
    id: 1,
    label: 'Shipment manager',
    checked: false
  },
  {
    id: 2,
    label: 'Site manager',
    checked: false
  },
  {
    id: 3,
    label: 'Audit trail',
    checked: false
  },
  {
    id: 4,
    label: 'Address book',
    checked: false
  }
];

const modulesIdMap = {
  SHIPMENT: 1,
  SITE: 2,
  AUDIT_TRAIL: 3,
  ADDRESS_BOOK: 4
};

export const isEnabledModule = (moduleName, modules) => {
  const selectedModule = modules?.filter(m => m.id === modulesIdMap[moduleName])?.[0];

  if (!selectedModule?.checked) {
    return false;
  }

  return !selectedModule?.disabled;
};

export const isEnabledModuleById = (modules, id) =>
  modules?.filter(fm => fm.id === id).length > 0;

export const getInitialModules = (currentProject = {}, parentProject = {}, isRootProject) => {
  const currentProjectExist = currentProject && Object.keys(currentProject).length > 0;
  const parentModules = parentProject?.modules;

  let res;

  if (currentProjectExist) {
    res = initialModules.map(iM => {
      const isEnabledInParent = isEnabledModuleById(parentModules, iM.id);

      if (isEnabledModuleById(currentProject?.modules, iM.id)) {
        return ({
          ...iM,
          checked: true
        });
      }

      return ({
        ...iM,
        disabled: !isEnabledInParent && !isRootProject
      });
    });
  } else {
    res = initialModules.map(iM => {
      const activeModule = parentModules?.find(parentModule => parentModule.id === iM.id);

      if (activeModule) {
        return {
          ...iM,
          checked: true
        };
      }

      return ({
        ...iM,
        disabled: true
      });
    });
  }

  return res;
};

const getFormattedCustomFieldValue = value => {
  if (isUndefinedOrNull(value)) {
    return undefined;
  }

  return Array.isArray(value) ?
    value.map(v => ({ customFieldValue: v })) : [{ customFieldValue: value }];
};

export const customFieldsToInitValues = customFields =>
  customFields?.map(customField => {
    const { label, id: customFieldId, value } = customField;

    return {
      label,
      id: customFieldId,
      customFieldValues: getFormattedCustomFieldValue(value)
    };
  });

export const formatAlertDelayIntoFullTime = projectAlerts => {
  const keys = ['startDelay', 'minAlertPeriod'];
  const formattedAlerts = projectAlerts.map(alert => {
    const transformedAlert = { ...alert };
    Object.keys(alert).forEach(key => {
      if (keys.includes(key) && !isUndefinedOrNull(alert[key]) && alert[key] !== '') {
        if (key === 'startDelay' || key === 'minAlertPeriod') {
          const alertTimeInMilliseconds = Number(alert[key]) * 60000;
          // Convert startDelay or minAlertPeriod to { days, hour, minutes } format.
          transformedAlert[key] = getFullTimeFromTimeDelta(alertTimeInMilliseconds);
        }
      }
    });

    return transformedAlert;
  });

  return formattedAlerts;
};

// Which form fields belong to which wizard step. Drives the per-step validity
// gate on Next/Back and the "completed" marks in the stepper.
export const STEP_FIELDS = {
  project: [
    'parentId',
    'title',
    'beginDate',
    'isExpirationDate',
    'expirationDate',
    'devices',
    'users',
    'description',
    'modules'
  ],
  alerts: ['alerts'],
  alertConfirmation: ['alertConfirmations'],
  products: ['products'],
  customFields: ['customFields'],
  packageType: ['packages']
};

const hasTruthyLeaf = node => {
  if (Array.isArray(node)) {
    return node.some(hasTruthyLeaf);
  }
  if (node && typeof node === 'object') {
    return Object.values(node).some(hasTruthyLeaf);
  }
  return Boolean(node);
};

export const isStepValid = (stepName, errors = {}) =>
  !STEP_FIELDS[stepName]?.some(field => hasTruthyLeaf(errors[field]));

export const isStepDirty = (stepName, dirtyFields = {}) =>
  Boolean(STEP_FIELDS[stepName]?.some(field => hasTruthyLeaf(dirtyFields[field])));
